/**
 * Username generation and validation service
 * Handles username collision detection and alternative generation
 */

import { supabase } from '@/integrations/supabase/client';

export interface UsernameValidationResult {
  isAvailable: boolean;
  suggestedAlternatives?: string[];
  checkedUsername: string;
}

export interface UsernameGenerationResult {
  username: string;
  isOriginal: boolean;
  alternatives: string[];
}

/**
 * Generate a base username from email
 */
export const generateBaseUsername = (email: string): string => {
  return email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
};

/**
 * Check if a username is available in the database
 */
export const checkUsernameAvailability = async (username: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('username')
      .eq('username', username)
      .maybeSingle();

    if (error) {
      console.error('Error checking username availability:', error);
      // If there's an error, assume username is taken to be safe
      return false;
    }

    // Username is available if no record was found
    return data === null;
  } catch (error) {
    console.error('Error in username availability check:', error);
    return false;
  }
};

/**
 * Generate alternative usernames when the original is taken
 */
export const generateUsernameAlternatives = (baseUsername: string, count: number = 5): string[] => {
  const alternatives: string[] = [];
  
  // Strategy 1: Add numbers (1-99)
  for (let i = 1; i <= Math.min(count, 10); i++) {
    alternatives.push(`${baseUsername}${i}`);
  }
  
  // Strategy 2: Add random numbers (100-999)
  while (alternatives.length < count) {
    const randomNum = Math.floor(Math.random() * 900) + 100;
    const alternative = `${baseUsername}${randomNum}`;
    if (!alternatives.includes(alternative)) {
      alternatives.push(alternative);
    }
  }
  
  return alternatives.slice(0, count);
};

/**
 * Find the first available username from a list of candidates
 */
export const findAvailableUsername = async (candidates: string[]): Promise<string | null> => {
  for (const candidate of candidates) {
    const isAvailable = await checkUsernameAvailability(candidate);
    if (isAvailable) {
      return candidate;
    }
  }
  return null;
};

/**
 * Generate a unique username with collision handling
 */
export const generateUniqueUsername = async (email: string): Promise<UsernameGenerationResult> => {
  const baseUsername = generateBaseUsername(email);
  
  // First, check if the base username is available
  const isBaseAvailable = await checkUsernameAvailability(baseUsername);
  
  if (isBaseAvailable) {
    return {
      username: baseUsername,
      isOriginal: true,
      alternatives: []
    };
  }
  
  // If base username is taken, generate alternatives
  const alternatives = generateUsernameAlternatives(baseUsername, 10);
  const availableUsername = await findAvailableUsername(alternatives);
  
  if (availableUsername) {
    return {
      username: availableUsername,
      isOriginal: false,
      alternatives: alternatives.filter(alt => alt !== availableUsername)
    };
  }
  
  // If no alternatives are available, generate a fallback with timestamp
  const timestamp = Date.now().toString().slice(-6);
  const fallbackUsername = `${baseUsername}${timestamp}`;
  
  return {
    username: fallbackUsername,
    isOriginal: false,
    alternatives: alternatives
  };
};

/**
 * Validate username format and availability
 */
export const validateUsername = async (username: string): Promise<UsernameValidationResult> => {
  // Basic format validation
  if (!username || username.length < 3) {
    return {
      isAvailable: false,
      checkedUsername: username,
      suggestedAlternatives: []
    };
  }
  
  if (username.length > 50) {
    return {
      isAvailable: false,
      checkedUsername: username,
      suggestedAlternatives: []
    };
  }
  
  // Check for valid characters (alphanumeric only)
  if (!/^[a-z0-9]+$/.test(username)) {
    return {
      isAvailable: false,
      checkedUsername: username,
      suggestedAlternatives: []
    };
  }
  
  // Check availability in database
  const isAvailable = await checkUsernameAvailability(username);
  
  if (!isAvailable) {
    // Generate suggestions if username is taken
    const alternatives = generateUsernameAlternatives(username, 3);
    const availableAlternatives: string[] = [];
    
    for (const alt of alternatives) {
      const altAvailable = await checkUsernameAvailability(alt);
      if (altAvailable) {
        availableAlternatives.push(alt);
      }
    }
    
    return {
      isAvailable: false,
      checkedUsername: username,
      suggestedAlternatives: availableAlternatives
    };
  }
  
  return {
    isAvailable: true,
    checkedUsername: username,
    suggestedAlternatives: []
  };
};

/**
 * Debounced username validation for real-time checking
 */
export class DebouncedUsernameValidator {
  private timeoutId: NodeJS.Timeout | null = null;
  private readonly delay: number;
  
  constructor(delay: number = 500) {
    this.delay = delay;
  }
  
  validate(username: string, callback: (result: UsernameValidationResult) => void): void {
    // Clear previous timeout
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    
    // Set new timeout
    this.timeoutId = setTimeout(async () => {
      try {
        const result = await validateUsername(username);
        callback(result);
      } catch (error) {
        console.error('Error in debounced username validation:', error);
        callback({
          isAvailable: false,
          checkedUsername: username,
          suggestedAlternatives: []
        });
      }
    }, this.delay);
  }
  
  cancel(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}

/**
 * Create a Supabase edge function for username checking (for server-side validation)
 */
export const createUsernameCheckFunction = () => {
  return `
import { serve } from "https://deno.land/std@0.168.0/http/server.ts"
import { createClient } from 'https://esm.sh/@supabase/supabase-js@2'

const corsHeaders = {
  'Access-Control-Allow-Origin': '*',
  'Access-Control-Allow-Headers': 'authorization, x-client-info, apikey, content-type',
}

serve(async (req) => {
  if (req.method === 'OPTIONS') {
    return new Response('ok', { headers: corsHeaders })
  }

  try {
    const { username } = await req.json()
    
    if (!username) {
      return new Response(
        JSON.stringify({ error: 'Username is required' }),
        { status: 400, headers: { ...corsHeaders, 'Content-Type': 'application/json' } }
      )
    }

    const supabase = createClient(
      Deno.env.get('SUPABASE_URL') ?? '',
      Deno.env.get('SUPABASE_SERVICE_ROLE_KEY') ?? ''
    )

    const { data, error } = await supabase
      .from('profiles')
      .select('username')
      .eq('username', username)
      .maybeSingle()

    if (error) {
      return new Response(
        JSON.stringify({ error: 'Database error' }),
        { status: 500, headers: { ...corsHeaders, 'Content-Type': 'application/json' } }
      )
    }

    return new Response(
      JSON.stringify({ 
        isAvailable: data === null,
        username: username
      }),
      { headers: { ...corsHeaders, 'Content-Type': 'application/json' } }
    )

  } catch (error) {
    return new Response(
      JSON.stringify({ error: 'Internal server error' }),
      { status: 500, headers: { ...corsHeaders, 'Content-Type': 'application/json' } }
    )
  }
})
`;
};
